import HitRecord from "models/view-models/search/hit-record";
import { RefObject, useEffect } from "react";
import { useLocation } from "react-router";
import { CollectionUtils } from "utilities/collection-utils";
import HighlightUtils, { DATA_NO_HIGHLIGHT } from "utilities/highlight-utils";

const BOOKVIEW_HIGHLIGHT_NAME = "bookview-search-results";

interface UseBookviewSearchHighlightsOptions {
    searchTerm: string;
    searchResults?: HitRecord[];
    enable?: boolean;
    contentRef?: RefObject<HTMLElement>;
}

const useBookviewSearchHighlights = ({
    searchTerm,
    enable,
    contentRef,
    searchResults = [],
}: UseBookviewSearchHighlightsOptions) => {
    const location = useLocation();

    // Adds and removes highlighting based on panel status
    useEffect(() => {
        const updateHighlights = () => {
            if (
                CollectionUtils.hasValues(searchResults) &&
                searchTerm &&
                enable &&
                contentRef &&
                !!contentRef.current
            ) {
                addHighlights(searchTerm, contentRef);
            } else {
                removeHighlights();
            }
        };

        const observer = new MutationObserver(updateHighlights);
        if (contentRef?.current) {
            observer.observe(contentRef.current, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }
        updateHighlights();
        return () => {
            removeHighlights();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentRef, enable, searchResults, location]);
};

const addHighlights = async (
    searchTerm: string,
    ref: RefObject<HTMLElement | undefined>
) => {
    const unfilteredRanges =
        HighlightUtils.buildRangesForEntireBookviewBasedOnSearchTerm(
            ref,
            searchTerm
        );

    const ranges = unfilteredRanges.flatMap((range) =>
        HighlightUtils.getFilteredRanges(range, `[${DATA_NO_HIGHLIGHT}]`)
    );

    HighlightUtils.registerCSSHighlightFromRange(
        BOOKVIEW_HIGHLIGHT_NAME,
        ranges
    );
};

const removeHighlights = () => {
    CSS.highlights.delete(BOOKVIEW_HIGHLIGHT_NAME);
};

export default useBookviewSearchHighlights;
